import { Image as ImageIcon, Video, FileText, Zap, Target, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageType } from '@/types';

interface DashboardHomeProps {
  onPageChange?: (page: PageType) => void;
  isDarkMode: boolean;
}

export default function DashboardHome({ isDarkMode }: DashboardHomeProps) {
  const features = [
    {
      id: 'images' as PageType,
      path: '/images',
      title: 'Conversor de Imagens',
      description: 'Redimensione e converta imagens entre mais de 80 formatos diferentes com qualidade profissional.',
      icon: ImageIcon,
      stats: '80+ formatos suportados',
      bgColor: isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50',
      textColor: isDarkMode ? 'text-blue-400' : 'text-blue-600'
    },
    {
      id: 'videos' as PageType,
      path: '/videos',
      title: 'Conversor de Vídeos',
      description: 'Converta vídeos entre os principais formatos como MP4, WebM, MOV e mais com compressão inteligente.',
      icon: Video,
      stats: '5+ formatos populares',
      bgColor: isDarkMode ? 'bg-purple-500/10' : 'bg-purple-50',
      textColor: isDarkMode ? 'text-purple-400' : 'text-purple-600'
    },
    {
      id: 'documents' as PageType,
      path: '/documents',
      title: 'Conversor de Documentos',
      description: 'Transforme documentos entre PDF, Word, RTF e outros formatos mantendo a formatação original.',
      icon: FileText,
      stats: '15+ formatos de documento',
      bgColor: isDarkMode ? 'bg-green-500/10' : 'bg-green-50',
      textColor: isDarkMode ? 'text-green-400' : 'text-green-600'
    }
  ];

  const highlights = [
    {
      icon: Zap,
      title: 'Processamento Rápido',
      description: 'Algoritmos otimizados para conversão em alta velocidade'
    },
    {
      icon: Target,
      title: 'Precisão Profissional',
      description: 'Mantenha a qualidade original com controle avançado'
    },
    {
      icon: Sparkles,
      title: 'Tech Solutions Pro',
      description: 'Tecnologia profissional para melhores resultados'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div className="text-center space-y-3">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <div className="relative w-16 h-16 transform-gpu animate-ai-core-fast">
              <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full animate-ai-pulse-fast shadow-lg ${
                isDarkMode 
                  ? 'bg-purple-500 shadow-purple-500/30'
                  : 'bg-purple-700 shadow-purple-700/40'
              }`}></div>
              <div className={`absolute inset-0 w-16 h-16 border-2 border-transparent rounded-full animate-neural-ring-1-fast ${
                isDarkMode 
                  ? 'border-t-cyan-400/40 border-r-blue-500/40' 
                  : 'border-t-cyan-600/40 border-r-blue-700/40'
              }`}></div>
              <div className={`absolute inset-2 w-12 h-12 border-2 border-transparent rounded-full animate-neural-ring-2-fast ${
                isDarkMode 
                  ? 'border-b-purple-600/40 border-l-cyan-400/40' 
                  : 'border-b-purple-800/40 border-l-cyan-600/40'
              }`}></div>
            </div>
          </div>
        </div>
        
        <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-700'}`}>
          Bem-vindo ao ConverterPro
        </h1>
        <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} max-w-xl mx-auto`}>
          Software completo para conversão de arquivos desenvolvido pela Tech Solutions Pro. Converta imagens, vídeos e documentos com qualidade profissional.
        </p>
      </div>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {features.map((feature) => {
          const Icon = feature.icon;
          return (
            <Link
              key={feature.id}
              to={feature.path}
              className={`group cursor-pointer p-3 rounded-xl border transition-all duration-300 hover:shadow-lg hover:scale-102 active:scale-95 block ${
                isDarkMode 
                  ? 'bg-[#2A2E3F] border-[#4A4F64] hover:border-[#52576B] hover:shadow-cyan-500/20' 
                  : 'bg-white border-[#E5E5E5] hover:border-[#D0D0D0] hover:shadow-lg'
              }`}
            >
              <div className={`w-10 h-10 rounded-lg ${feature.bgColor} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300`}>
                <Icon className={`w-5 h-5 ${feature.textColor}`} />
              </div>
              
              <h3 className={`text-base font-semibold mb-1.5 group-hover:text-cyan-600 transition-colors duration-300 ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                {feature.title}
              </h3>
              
              <p className={`text-xs mb-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {feature.description}
              </p>
              
              <div className="flex items-center justify-between">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                  feature.bgColor
                } ${feature.textColor}`}>
                  {feature.stats}
                </div>
                
                <div className={`opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs font-medium ${
                  isDarkMode ? 'text-cyan-400' : 'text-cyan-600'
                }`}>
                  Clique para abrir →
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Highlights */}
      <div className={`p-6 rounded-xl border ${
        isDarkMode 
          ? 'bg-[#383D52] border-[#4A4F64]' 
          : 'bg-white border-[#E5E5E5]'
      }`}>
        <h2 className={`text-xl font-bold text-center mb-6 ${
          isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
        }`}>
          Por que escolher o ConverterPro?
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {highlights.map((highlight, index) => {
            const Icon = highlight.icon;
            return (
              <div key={index} className="text-center space-y-2">
                <div className={`w-10 h-10 rounded-lg mx-auto flex items-center justify-center ${
                  isDarkMode ? 'bg-gradient-to-br from-cyan-500/20 to-purple-600/20' : 'bg-gradient-to-br from-cyan-100 to-purple-100'
                }`}>
                  <Icon className={`w-5 h-5 ${
                    isDarkMode ? 'text-cyan-400' : 'text-cyan-600'
                  }`} />
                </div>
                
                <h3 className={`font-semibold ${
                  isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                }`}>
                  {highlight.title}
                </h3>
                
                <p className={`text-sm ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {highlight.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>


    </div>
  );
}
